/**
 * SDR Stock Manager - LocalStorage Persistence Adapter
 * @module state/storage
 */

import { STORAGE_KEYS, DEFAULT_SETTINGS } from '../config/constants.js';
import { getState, setState } from './store.js';

/**
 * Checks if an error is a browser storage quota exceeded error.
 * @param {any} err
 * @returns {boolean}
 */
export function isQuotaExceededError(err) {
  if (!err) return false;
  return (
    err.name === 'QuotaExceededError' ||
    err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    err.code === 22 ||
    err.code === 1014 ||
    (typeof err.message === 'string' && err.message.toLowerCase().includes('quota'))
  );
}

/**
 * Safely reads a raw or JSON-parsed value from localStorage.
 * @template T
 * @param {string} key
 * @param {T} [defaultValue=null]
 * @returns {T|any}
 */
export function getStorageItem(key, defaultValue = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === undefined) return defaultValue;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch (err) {
    console.warn(`LocalStorage read failed for key "${key}":`, err);
    return defaultValue;
  }
}

/**
 * Strips Base64 data URL images from inventory items to fit within localStorage quota.
 * Standard HTTPS image URLs are kept intact.
 * @param {Array<any>} inventory
 * @returns {Array<any>}
 */
export function sanitizeInventoryForStorage(inventory) {
  if (!Array.isArray(inventory)) return [];
  return inventory.map(item => {
    if (item && item.image && typeof item.image === 'string' && item.image.startsWith('data:image')) {
      return { ...item, image: '' };
    }
    return item;
  });
}

/**
 * Safely writes a value to localStorage with QuotaExceededError recovery.
 * @param {string} key
 * @param {any} value
 * @returns {boolean} True if saved successfully, false on error
 */
export function setStorageItem(key, value) {
  try {
    const str = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, str);
    return true;
  } catch (err) {
    if (isQuotaExceededError(err)) {
      // 1. If inventory write failed from quota, strip large Base64 images and retry
      if (key === STORAGE_KEYS.INVENTORY && Array.isArray(value)) {
        try {
          const sanitized = sanitizeInventoryForStorage(value);
          const cleanStr = JSON.stringify(sanitized);
          localStorage.setItem(key, cleanStr);
          console.warn(`LocalStorage quota exceeded: preserved inventory by stripping Base64 images for "${key}".`);
          return true;
        } catch (err2) {
          if (!isQuotaExceededError(err2)) {
            console.warn(`LocalStorage sanitized write failed for "${key}":`, err2);
            return false;
          }
        }
      }

      // 2. Try clearing old backups cache to free quota space and retry
      try {
        localStorage.removeItem(STORAGE_KEYS.BACKUPS);
        let retryVal = value;
        if (key === STORAGE_KEYS.INVENTORY && Array.isArray(value)) {
          retryVal = sanitizeInventoryForStorage(value);
        }
        const retryStr = typeof retryVal === 'string' ? retryVal : JSON.stringify(retryVal);
        localStorage.setItem(key, retryStr);
        console.warn(`LocalStorage quota exceeded: cleared backups and preserved "${key}".`);
        return true;
      } catch (err3) {
        // Fallback failed
      }
    }
    console.warn(`LocalStorage write failed for key "${key}":`, err);
    return false;
  }
}

/**
 * Safely removes an item from localStorage.
 * @param {string} key
 */
export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn(`LocalStorage remove failed for key "${key}":`, err);
  }
}

let pendingSaveTimeout = null;
let pendingSaveKeys = new Set();

/**
 * Saves specific changed keys or all keys to localStorage.
 * @param {string[]|Set<string>|null} [keys=null]
 * @param {Partial<import('../types/index.js').AppState>} [stateOverride=null]
 * @returns {boolean} Success status
 */
export function saveLocalDataKeys(keys = null, stateOverride = null) {
  const currentState = stateOverride || getState();
  const keysSet = keys ? new Set(keys) : null;
  const saveAll = !keysSet;

  try {
    if (saveAll || keysSet.has('inventory')) {
      setStorageItem(STORAGE_KEYS.INVENTORY, currentState.inventory || []);
    }
    if (saveAll || keysSet.has('transactions')) {
      setStorageItem(STORAGE_KEYS.TRANSACTIONS, currentState.transactions || []);
    }
    if (saveAll || keysSet.has('stockAudits')) {
      setStorageItem(STORAGE_KEYS.STOCK_AUDITS, currentState.stockAudits || []);
    }
    if (saveAll || keysSet.has('stockOutMaster')) {
      setStorageItem(STORAGE_KEYS.STOCK_OUT_MASTER, currentState.stockOutMaster || []);
    }
    if (saveAll || keysSet.has('stockOutDetails')) {
      setStorageItem(STORAGE_KEYS.STOCK_OUT_DETAILS, currentState.stockOutDetails || []);
    }

    if ((saveAll || keysSet.has('settings')) && currentState.settings) {
      setStorageItem(STORAGE_KEYS.SETTINGS, currentState.settings);
      // Also write legacy keys for backwards compatibility
      setStorageItem(STORAGE_KEYS.LEGACY_SHEET_URL, currentState.settings.sheetUrl || '');
      setStorageItem(STORAGE_KEYS.LEGACY_SUPABASE_URL, currentState.settings.supabaseUrl || '');
      setStorageItem(STORAGE_KEYS.LEGACY_SUPABASE_ANON_KEY, currentState.settings.supabaseAnonKey || '');
      setStorageItem(STORAGE_KEYS.LEGACY_R2_UPLOAD_URL, currentState.settings.r2UploadUrl || '');
      setStorageItem(STORAGE_KEYS.LEGACY_LOW_STOCK_THRESHOLD, String(currentState.settings.lowStockThreshold ?? 5));
      setStorageItem(STORAGE_KEYS.LEGACY_EXPIRY_WARNING_DATE, currentState.settings.expiryWarningDate || '');
      setStorageItem(STORAGE_KEYS.LEGACY_STORAGE_LOCATIONS, currentState.settings.storageLocations || '');
    }

    if (saveAll || keysSet.has('activeAuditId')) {
      if (currentState.activeAuditId) {
        setStorageItem(STORAGE_KEYS.ACTIVE_AUDIT_ID, currentState.activeAuditId);
      } else {
        removeStorageItem(STORAGE_KEYS.ACTIVE_AUDIT_ID);
      }
    }

    if ((saveAll || keysSet.has('lastCloudSyncAt')) && currentState.lastCloudSyncAt) {
      setStorageItem(STORAGE_KEYS.LAST_SYNC_AT, currentState.lastCloudSyncAt);
    }
    return true;
  } catch (err) {
    console.warn('LocalStorage save failed, falling back to memory-only mode:', err);
    return false;
  }
}

/**
 * Flushes any pending debounced localStorage save immediately.
 */
export function flushPendingLocalDataSave() {
  if (pendingSaveTimeout) {
    clearTimeout(pendingSaveTimeout);
    pendingSaveTimeout = null;
  }
  if (pendingSaveKeys.size > 0) {
    const keysToSave = new Set(pendingSaveKeys);
    pendingSaveKeys.clear();
    saveLocalDataKeys(keysToSave);
  }
}

/**
 * Schedules a debounced asynchronous localStorage save to avoid blocking user interactions.
 * @param {string[]|string|null} [changedKeys=null]
 */
export function scheduleSaveLocalData(changedKeys = null) {
  if (changedKeys) {
    const arr = Array.isArray(changedKeys) ? changedKeys : [changedKeys];
    arr.forEach(k => pendingSaveKeys.add(k));
  } else {
    pendingSaveKeys.add('inventory');
    pendingSaveKeys.add('transactions');
    pendingSaveKeys.add('stockAudits');
    pendingSaveKeys.add('stockOutMaster');
    pendingSaveKeys.add('stockOutDetails');
    pendingSaveKeys.add('settings');
    pendingSaveKeys.add('activeAuditId');
    pendingSaveKeys.add('lastCloudSyncAt');
  }

  if (pendingSaveTimeout) return;

  const runSave = () => {
    pendingSaveTimeout = null;
    if (pendingSaveKeys.size > 0) {
      const keysToSave = new Set(pendingSaveKeys);
      pendingSaveKeys.clear();
      saveLocalDataKeys(keysToSave);
    }
  };

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    pendingSaveTimeout = setTimeout(() => {
      window.requestIdleCallback(runSave, { timeout: 300 });
    }, 50);
  } else {
    pendingSaveTimeout = setTimeout(runSave, 50);
  }
}

/**
 * Saves current store state (or provided state) to localStorage synchronously.
 * @param {Partial<import('../types/index.js').AppState>} [stateOverride]
 * @returns {boolean} Success status
 */
export function saveLocalData(stateOverride = null) {
  if (pendingSaveTimeout) {
    clearTimeout(pendingSaveTimeout);
    pendingSaveTimeout = null;
    pendingSaveKeys.clear();
  }
  return saveLocalDataKeys(null, stateOverride);
}

/**
 * Loads persisted data from localStorage into the store (or returns the parsed data).
 * @param {boolean} [applyToStore=true] If true, applies loaded state to store singleton.
 * @returns {Partial<import('../types/index.js').AppState>}
 */
export function loadLocalData(applyToStore = true) {
  const loaded = {
    inventory: [],
    transactions: [],
    stockAudits: [],
    stockOutMaster: [],
    stockOutDetails: [],
    activeAuditId: null,
    settings: { ...DEFAULT_SETTINGS },
    lastCloudSyncAt: ''
  };

  try {
    const localInv = getStorageItem(STORAGE_KEYS.INVENTORY, null);
    const localTrans = getStorageItem(STORAGE_KEYS.TRANSACTIONS, null);
    const localAudits = getStorageItem(STORAGE_KEYS.STOCK_AUDITS, null);
    const localStockOutMaster = getStorageItem(STORAGE_KEYS.STOCK_OUT_MASTER, null);
    const localStockOutDetails = getStorageItem(STORAGE_KEYS.STOCK_OUT_DETAILS, null);
    const localActiveAudit = localStorage.getItem(STORAGE_KEYS.ACTIVE_AUDIT_ID);
    const localLastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC_AT);

    if (Array.isArray(localInv)) loaded.inventory = localInv;
    if (Array.isArray(localTrans)) loaded.transactions = localTrans;
    if (Array.isArray(localAudits)) loaded.stockAudits = localAudits;
    if (Array.isArray(localStockOutMaster)) {
      loaded.stockOutMaster = localStockOutMaster.map(m => {
        if (!m || typeof m !== 'object') return m;
        const ts = m.timestamp || m.date || m.createdAt || '';
        const op = m.operator || m.operatorName || m.operator_name || '';
        return {
          ...m,
          timestamp: ts,
          date: m.date || (ts ? ts.split('T')[0] : ''),
          createdAt: m.createdAt || ts,
          operator: op,
          operatorName: m.operatorName || op
        };
      });
    }
    if (Array.isArray(localStockOutDetails)) {
      loaded.stockOutDetails = localStockOutDetails.map(d => {
        if (!d || typeof d !== 'object') return d;
        const sId = d.stockOutId || d.masterId || d.stock_out_id || '';
        return {
          ...d,
          stockOutId: sId,
          masterId: sId,
          itemName: d.itemName || d.item_name || '',
          quantity: Number(d.quantity) || 0
        };
      });
    }
    if (localActiveAudit) loaded.activeAuditId = localActiveAudit;
    if (localLastSync) loaded.lastCloudSyncAt = localLastSync;

    // Load structured settings or legacy settings
    const savedSettings = getStorageItem(STORAGE_KEYS.SETTINGS, null);
    if (savedSettings && typeof savedSettings === 'object') {
      loaded.settings = { ...DEFAULT_SETTINGS, ...savedSettings };
    } else {
      // Legacy fallback
      const sheetUrl = localStorage.getItem(STORAGE_KEYS.LEGACY_SHEET_URL);
      const supabaseUrl = localStorage.getItem(STORAGE_KEYS.LEGACY_SUPABASE_URL);
      const anonKey = localStorage.getItem(STORAGE_KEYS.LEGACY_SUPABASE_ANON_KEY);
      const r2Url = localStorage.getItem(STORAGE_KEYS.LEGACY_R2_UPLOAD_URL);
      const lowStock = localStorage.getItem(STORAGE_KEYS.LEGACY_LOW_STOCK_THRESHOLD);
      const warningDate = localStorage.getItem(STORAGE_KEYS.LEGACY_EXPIRY_WARNING_DATE);
      const locations = localStorage.getItem(STORAGE_KEYS.LEGACY_STORAGE_LOCATIONS);

      if (sheetUrl) loaded.settings.sheetUrl = sheetUrl;
      if (supabaseUrl) loaded.settings.supabaseUrl = supabaseUrl;
      if (anonKey) loaded.settings.supabaseAnonKey = anonKey;
      if (r2Url) loaded.settings.r2UploadUrl = r2Url;
      if (lowStock) loaded.settings.lowStockThreshold = parseInt(lowStock, 10) || 5;
      if (warningDate) loaded.settings.expiryWarningDate = warningDate;
      if (locations) loaded.settings.storageLocations = locations;
    }
  } catch (err) {
    console.warn('LocalStorage load failed, falling back to memory-only mode:', err);
  }

  if (applyToStore) {
    setState(loaded, { silent: false });
  }

  return loaded;
}

/**
 * Purges all business data from localStorage and resets store state.
 */
export function clearLocalData() {
  removeStorageItem(STORAGE_KEYS.INVENTORY);
  removeStorageItem(STORAGE_KEYS.TRANSACTIONS);
  removeStorageItem(STORAGE_KEYS.STOCK_AUDITS);
  removeStorageItem(STORAGE_KEYS.STOCK_OUT_MASTER);
  removeStorageItem(STORAGE_KEYS.STOCK_OUT_DETAILS);
  removeStorageItem(STORAGE_KEYS.ACTIVE_AUDIT_ID);
  removeStorageItem(STORAGE_KEYS.LAST_SYNC_AT);
  removeStorageItem(STORAGE_KEYS.SESSION);
  removeStorageItem(STORAGE_KEYS.USER);

  // Clear any Supabase auth tokens
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  } catch (err) {
    console.warn('Error clearing Supabase auth tokens:', err);
  }
}
